import { db } from '../firebase';
import {
  doc,
  runTransaction,
  serverTimestamp,
  increment,
  setDoc
} from 'firebase/firestore';

const GROUPS_COLLECTION = 'newsletter_groups';
const CUSTOMERS_GROUP_ID = 'clientes';
const MEMBERS_SUB = 'members';

export function normalizeNewsletterEmail(value) {
  if (typeof value !== 'string') return '';
  const collapsed = value
    .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();
  try {
    return collapsed.normalize('NFKC');
  } catch {
    return collapsed;
  }
}

/** Misma lógica que functions/newsletter.js para unificar id de miembro y evitar duplicados. */
export function canonicalNewsletterEmail(value) {
  const n = normalizeNewsletterEmail(value);
  if (!n || n.indexOf('@') < 1) return n;
  const at = n.lastIndexOf('@');
  let local = n.slice(0, at);
  let domain = n.slice(at + 1).toLowerCase();
  if (domain === 'googlemail.com') domain = 'gmail.com';
  const plusIdx = local.indexOf('+');
  if (plusIdx !== -1) local = local.slice(0, plusIdx);
  if (domain === 'gmail.com') {
    local = local.replace(/\./g, '');
  }
  return `${local}@${domain}`;
}

export function isValidNewsletterEmail(value) {
  const n = normalizeNewsletterEmail(value);
  return n.length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(n);
}

export async function emailDocIdForNewsletter(email) {
  const canon = canonicalNewsletterEmail(email);
  const data = new TextEncoder().encode(canon);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hex.slice(0, 40);
}

/** Mismos campos que el servidor (ventas_fisicas, pedidos, MP). */
export function extractEmailsFromSaleRecord(data) {
  if (!data || typeof data !== 'object') return [];
  const raw = [
    data.email,
    data.Email,
    data.mail,
    data.buyer_email,
    data.customerEmail,
    data.customerData?.email,
    data.customerData?.Email,
    data.customerData?.mail,
    data.invoiceOptions?.emailAddress,
    data.invoiceOptions?.email,
    data.payer?.email,
    data.cliente?.email
  ];
  const seen = new Set();
  const result = [];
  for (const candidate of raw) {
    const norm = normalizeNewsletterEmail(candidate);
    if (!norm || !isValidNewsletterEmail(norm)) continue;
    const canon = canonicalNewsletterEmail(norm);
    if (!seen.has(canon)) {
      seen.add(canon);
      result.push(canon);
    }
  }
  return result;
}

/**
 * Agrega emails del cliente al grupo newsletter "clientes" (1 escritura si es nuevo).
 * Llamar tras confirmar venta física con email.
 */
export async function registerCustomerNewsletterEmails(data, source = 'ventas_fisicas') {
  const emails = extractEmailsFromSaleRecord(data);
  if (emails.length === 0) return { added: 0 };

  const groupRef = doc(db, GROUPS_COLLECTION, CUSTOMERS_GROUP_ID);
  let added = 0;

  for (const email of emails) {
    const memberId = await emailDocIdForNewsletter(email);
    const memberRef = doc(groupRef, MEMBERS_SUB, memberId);
    const isNew = await runTransaction(db, async (tx) => {
      const snap = await tx.get(memberRef);
      if (snap.exists()) return false;
      tx.set(memberRef, { email, addedAt: serverTimestamp(), source });
      return true;
    });
    if (isNew) added += 1;
  }

  if (added > 0) {
    await setDoc(
      groupRef,
      {
        name: 'Base de clientes',
        auto: true,
        autoSource: 'customers',
        membersMigrated: true,
        emailCount: increment(added),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  }

  return { added };
}
